import { useState, useMemo, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, AlertTriangle, HandCoins, Briefcase } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { formatDateDDMMYYYY, formatCurrency, cn } from "@/lib/utils";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { TransactionStatusBadge } from "@/components/transactions/transaction-status-badge";
import { Badge } from "@/components/ui/badge";
import type { Transaction, ColumnVisibility } from "@/types/transaction";
import type { ColumnDefinition } from "@/components/shared/column-config-dialog";

type SortColumn = keyof Transaction | null;
type SortDirection = "asc" | "desc";

/** Metadata for each transaction column used when rendering in dynamic order */
interface ColumnMeta {
    sortKey?: keyof Transaction;
    className?: string;
}

const COLUMN_META: Record<string, ColumnMeta> = {
    referenceNumber: { sortKey: "referenceNumber" },
    transactionDate: { sortKey: "transactionDate" },
    dueDate: { sortKey: "dueDate" },
    customerName: { sortKey: "customerName" },
    transactionType: { sortKey: "transactionType" },
    currency: { sortKey: "currency" },
    amount: { sortKey: "amount", className: "text-right" },
    outstandingAmount: { sortKey: "outstandingAmount", className: "text-right" },
    status: { sortKey: "status" },
    description: {},
    createdBy: {},
    modifiedBy: {},
    accountCode: { sortKey: "accountCode" },
    netAmount: { sortKey: "netAmount", className: "text-right" },
    vatAmount: { sortKey: "vatAmount", className: "text-right" },
    flags: {},
    onDispute: { sortKey: "onDispute" },
    subLedger: { sortKey: "subLedger" },
    orderNumber: { sortKey: "orderNumber" },
    currencyGross: { sortKey: "currencyGross", className: "text-right" },
    currencyNet: { sortKey: "currencyNet", className: "text-right" },
    currencyVAT: { sortKey: "currencyVAT", className: "text-right" },
    currencyOutstanding: { sortKey: "currencyOutstanding", className: "text-right" },
};

interface TransactionTableProps {
    transactions: Transaction[];
    columnVisibility: ColumnVisibility;
    orderedColumns?: ColumnDefinition[];
    onViewTransaction?: (transaction: Transaction) => void;
    showCustomerColumn?: boolean;
    showCompanyIdColumn?: boolean;
    enableSelection?: boolean;
    selectedRows?: Set<number>;
    onSelectRow?: (id: number, checked: boolean) => void;
    onSelectAll?: (checked: boolean) => void;
    currentPage?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    totalItems?: number;
    onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;
}

export function TransactionTable({
    transactions,
    columnVisibility,
    orderedColumns,
    onViewTransaction,
    showCustomerColumn = true,
    showCompanyIdColumn = false,
    enableSelection = false,
    selectedRows = new Set(),
    onSelectRow,
    onSelectAll,
    currentPage = 1,
    pageSize = 25,
    onPageChange,
    onPageSizeChange,
    totalItems,
    onSortChange,
}: TransactionTableProps) {
    const navigate = useNavigate();
    const [sortColumn, setSortColumn] = useState<SortColumn>("transactionDate");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    // When totalItems is provided, data is already server-side paginated
    const isServerPaginated = totalItems !== undefined;

    // Sort transactions — skip client-side sorting when server-paginated (server handles sort)
    const sortedTransactions = useMemo(() => {
        if (isServerPaginated) return transactions;
        if (!sortColumn) return transactions;

        return [...transactions].sort((a, b) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            if (aValue === undefined || aValue === null) return 1;
            if (bValue === undefined || bValue === null) return -1;

            let comparison = 0;
            if (typeof aValue === "string" && typeof bValue === "string") {
                comparison = aValue.localeCompare(bValue);
            } else if (typeof aValue === "number" && typeof bValue === "number") {
                comparison = aValue - bValue;
            }

            return sortDirection === "asc" ? comparison : -comparison;
        });
    }, [transactions, sortColumn, sortDirection, isServerPaginated]);

    // Paginate
    const paginatedTransactions = useMemo(() => {
        if (isServerPaginated) return sortedTransactions;
        const startIndex = (currentPage - 1) * pageSize;
        return sortedTransactions.slice(startIndex, startIndex + pageSize);
    }, [sortedTransactions, currentPage, pageSize, isServerPaginated]);

    const effectiveTotalItems = isServerPaginated ? totalItems : sortedTransactions.length;
    const totalPages = Math.ceil(effectiveTotalItems / pageSize);

    const handleSort = (column: SortColumn) => {
        let newDirection: SortDirection;
        if (sortColumn === column) {
            newDirection = sortDirection === "asc" ? "desc" : "asc";
        } else {
            newDirection = "asc";
        }
        setSortColumn(column);
        setSortDirection(newDirection);
        if (isServerPaginated && onSortChange && column) {
            onSortChange(column, newDirection);
        }
    };

    const handleSelectAll = (checked: boolean | "indeterminate") => {
        if (onSelectAll && checked === true) {
            onSelectAll(true);
        } else if (onSelectAll) {
            onSelectAll(false);
        }
    };

    const handleSelectRow = (id: number, checked: boolean | "indeterminate") => {
        if (onSelectRow && checked === true) {
            onSelectRow(id, true);
        } else if (onSelectRow) {
            onSelectRow(id, false);
        }
    };

    const renderSortIcon = (column: SortColumn) => {
        if (sortColumn !== column) return null;
        return sortDirection === "asc" ? (
            <ChevronUp className="h-4 w-4" />
        ) : (
            <ChevronDown className="h-4 w-4" />
        );
    };

    const renderSortableHeader = (column: keyof Transaction, label: string, visible: boolean, className?: string) => {
        if (!visible) return null;
        return (
            <TableHead
                key={column}
                className={cn("cursor-pointer", className)}
                onClick={() => handleSort(column)}
            >
                <div className={cn("flex items-center gap-1", className?.includes("text-right") && "justify-end")}>
                    {label}
                    {renderSortIcon(column)}
                </div>
            </TableHead>
        );
    };

    /** Render a single cell for the given column ID */
    const renderCell = useCallback((columnId: string, transaction: Transaction): ReactNode => {
        switch (columnId) {
            case "referenceNumber":
                return (
                    <TableCell key={columnId}>
                        <button
                            onClick={() => onViewTransaction?.(transaction)}
                            className="text-primary hover:underline font-medium"
                        >
                            {transaction.referenceNumber}
                        </button>
                    </TableCell>
                );
            case "transactionDate":
                return <TableCell key={columnId}>{formatDateDDMMYYYY(transaction.transactionDate)}</TableCell>;
            case "dueDate":
                return (
                    <TableCell key={columnId}>
                        {transaction.dueDate ? formatDateDDMMYYYY(transaction.dueDate) : "-"}
                    </TableCell>
                );
            case "customerName":
                if (!showCustomerColumn) return null;
                return (
                    <TableCell key={columnId}>
                        <button
                            onClick={() => navigate(`/customers/${transaction.customerId}`)}
                            className="text-primary hover:underline font-medium transition-colors"
                            title={`View details for ${transaction.customerName}`}
                        >
                            {transaction.customerName}
                        </button>
                    </TableCell>
                );
            case "transactionType":
                return (
                    <TableCell key={columnId}>
                        <TransactionTypeBadge type={transaction.transactionType} />
                    </TableCell>
                );
            case "currency":
                return <TableCell key={columnId}>{transaction.currency}</TableCell>;
            case "amount":
                return (
                    <TableCell key={columnId} className="text-right font-bold">
                        {formatCurrency(transaction.amount, transaction.currency)}
                    </TableCell>
                );
            case "outstandingAmount":
                return (
                    <TableCell key={columnId} className="text-right font-medium">
                        {transaction.outstandingAmount !== undefined
                            ? formatCurrency(transaction.outstandingAmount, transaction.currency)
                            : "-"}
                    </TableCell>
                );
            case "status":
                return (
                    <TableCell key={columnId}>
                        <TransactionStatusBadge status={transaction.status} />
                    </TableCell>
                );
            case "description":
                return (
                    <TableCell key={columnId} className="max-w-[300px] truncate">
                        {transaction.description}
                    </TableCell>
                );
            case "createdBy":
                return <TableCell key={columnId}>{transaction.createdBy}</TableCell>;
            case "modifiedBy":
                return <TableCell key={columnId}>{transaction.modifiedBy || "-"}</TableCell>;
            case "accountCode":
                return <TableCell key={columnId}>{transaction.accountCode || "-"}</TableCell>;
            case "netAmount":
                return (
                    <TableCell key={columnId} className="text-right">
                        {transaction.netAmount !== undefined
                            ? formatCurrency(transaction.netAmount, transaction.currency)
                            : "-"}
                    </TableCell>
                );
            case "vatAmount":
                return (
                    <TableCell key={columnId} className="text-right">
                        {transaction.vatAmount !== undefined
                            ? formatCurrency(transaction.vatAmount, transaction.currency)
                            : "-"}
                    </TableCell>
                );
            case "flags":
                return (
                    <TableCell key={columnId}>
                        <TooltipProvider>
                            <div className="flex items-center gap-2">
                                {transaction.onDispute && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 transition-colors cursor-help">
                                                <AlertTriangle className="h-4 w-4 text-red-600" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="bg-red-600 text-white border-red-700">
                                            <p className="font-semibold">Disputed</p>
                                            <p className="text-xs">This transaction is under dispute</p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                                {transaction.hasPromise && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors cursor-help">
                                                <HandCoins className="h-4 w-4 text-blue-600" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="bg-blue-600 text-white border-blue-700">
                                            <p className="font-semibold">Promise to Pay</p>
                                            <p className="text-xs">Customer has committed to payment</p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                                {transaction.hasCase && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 hover:bg-orange-200 transition-colors cursor-help">
                                                <Briefcase className="h-4 w-4 text-orange-600" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="bg-orange-600 text-white border-orange-700">
                                            <p className="font-semibold">Collection Case</p>
                                            <p className="text-xs">Part of an active collection case</p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                                {!transaction.onDispute && !transaction.hasPromise && !transaction.hasCase && (
                                    <span className="text-muted-foreground text-xs">-</span>
                                )}
                            </div>
                        </TooltipProvider>
                    </TableCell>
                );
            case "onDispute":
                return (
                    <TableCell key={columnId}>
                        {transaction.onDispute ? (
                            <Badge variant="destructive" className="text-xs">
                                Yes
                            </Badge>
                        ) : (
                            <span className="text-muted-foreground">No</span>
                        )}
                    </TableCell>
                );
            case "subLedger":
                return <TableCell key={columnId}>{transaction.subLedger || "-"}</TableCell>;
            case "orderNumber":
                return <TableCell key={columnId}>{transaction.orderNumber || "-"}</TableCell>;
            case "currencyGross":
                return (
                    <TableCell key={columnId} className="text-right">
                        {transaction.currencyGross != null
                            ? formatCurrency(transaction.currencyGross, transaction.currency)
                            : "-"}
                    </TableCell>
                );
            case "currencyNet":
                return (
                    <TableCell key={columnId} className="text-right">
                        {transaction.currencyNet != null
                            ? formatCurrency(transaction.currencyNet, transaction.currency)
                            : "-"}
                    </TableCell>
                );
            case "currencyVAT":
                return (
                    <TableCell key={columnId} className="text-right">
                        {transaction.currencyVAT != null
                            ? formatCurrency(transaction.currencyVAT, transaction.currency)
                            : "-"}
                    </TableCell>
                );
            case "currencyOutstanding":
                return (
                    <TableCell key={columnId} className="text-right">
                        {transaction.currencyOutstanding != null
                            ? formatCurrency(transaction.currencyOutstanding, transaction.currency)
                            : "-"}
                    </TableCell>
                );
            default:
                return null;
        }
    }, [onViewTransaction, showCustomerColumn, navigate]);

    // When orderedColumns is provided, compute visible columns sorted by order
    const visibleOrderedColumns = useMemo(() => {
        if (!orderedColumns) return null;
        return orderedColumns
            .filter((col) => {
                if (!col.visible) return false;
                // Skip customerName if showCustomerColumn is false
                if (col.id === "customerName" && !showCustomerColumn) return false;
                // Skip the actions column from table rendering
                if (col.id === "actions") return false;
                return true;
            })
            .sort((a, b) => a.order - b.order);
    }, [orderedColumns, showCustomerColumn]);

    const renderTransactionRow = (transaction: Transaction): ReactNode => (
        <TableRow key={transaction.id}>
            {enableSelection && (
                <TableCell>
                    <Checkbox
                        checked={selectedRows.has(transaction.id)}
                        onCheckedChange={(checked) =>
                            handleSelectRow(transaction.id, checked)
                        }
                    />
                </TableCell>
            )}
            {visibleOrderedColumns
                ? visibleOrderedColumns.map((col) => renderCell(col.id, transaction))
                : (
                    <>
                        {columnVisibility.referenceNumber && renderCell("referenceNumber", transaction)}
                        {columnVisibility.transactionDate && renderCell("transactionDate", transaction)}
                        {columnVisibility.dueDate && renderCell("dueDate", transaction)}
                        {showCustomerColumn && columnVisibility.customerName && renderCell("customerName", transaction)}
                        {columnVisibility.transactionType && renderCell("transactionType", transaction)}
                        {columnVisibility.currency && renderCell("currency", transaction)}
                        {columnVisibility.amount && renderCell("amount", transaction)}
                        {columnVisibility.outstandingAmount && renderCell("outstandingAmount", transaction)}
                        {columnVisibility.status && renderCell("status", transaction)}
                        {columnVisibility.description && renderCell("description", transaction)}
                        {columnVisibility.createdBy && renderCell("createdBy", transaction)}
                        {columnVisibility.modifiedBy && renderCell("modifiedBy", transaction)}
                        {columnVisibility.accountCode && renderCell("accountCode", transaction)}
                        {columnVisibility.netAmount && renderCell("netAmount", transaction)}
                        {columnVisibility.vatAmount && renderCell("vatAmount", transaction)}
                        {columnVisibility.flags && renderCell("flags", transaction)}
                        {columnVisibility.onDispute && renderCell("onDispute", transaction)}
                        {columnVisibility.subLedger && renderCell("subLedger", transaction)}
                        {columnVisibility.orderNumber && renderCell("orderNumber", transaction)}
                        {columnVisibility.currencyGross && renderCell("currencyGross", transaction)}
                        {columnVisibility.currencyNet && renderCell("currencyNet", transaction)}
                        {columnVisibility.currencyVAT && renderCell("currencyVAT", transaction)}
                        {columnVisibility.currencyOutstanding && renderCell("currencyOutstanding", transaction)}
                    </>
                )
            }
            {showCompanyIdColumn && (
                <TableCell>{`COMP-${Math.floor(Math.random() * 3) + 1}`}</TableCell>
            )}
        </TableRow>
    );

    /** Render a header for a single column definition using the column metadata registry */
    const renderOrderedHeader = (col: ColumnDefinition): ReactNode => {
        const meta = COLUMN_META[col.id];
        if (!meta) return <TableHead key={col.id}>{col.label}</TableHead>;
        if (meta.sortKey) {
            return renderSortableHeader(meta.sortKey, col.label, true, meta.className);
        }
        return <TableHead key={col.id}>{col.label}</TableHead>;
    };

    const visibleColumnCount = visibleOrderedColumns
        ? visibleOrderedColumns.length + (enableSelection ? 1 : 0) + (showCompanyIdColumn ? 1 : 0)
        : Object.values(columnVisibility).filter(Boolean).length + (enableSelection ? 1 : 0);

    return (
        <>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {enableSelection && (
                                <TableHead className="w-12">
                                    <Checkbox
                                        checked={
                                            paginatedTransactions.length > 0 &&
                                            paginatedTransactions.every((t) => selectedRows.has(t.id))
                                        }
                                        onCheckedChange={handleSelectAll}
                                    />
                                </TableHead>
                            )}
                            {visibleOrderedColumns
                                ? visibleOrderedColumns.map(renderOrderedHeader)
                                : (
                                    <>
                                        {renderSortableHeader("referenceNumber", "Reference Number", columnVisibility.referenceNumber)}
                                        {renderSortableHeader("transactionDate", "Transaction Date", columnVisibility.transactionDate)}
                                        {renderSortableHeader("dueDate", "Due Date", columnVisibility.dueDate)}
                                        {renderSortableHeader("customerName", "Customer Name", showCustomerColumn && columnVisibility.customerName)}
                                        {renderSortableHeader("transactionType", "Type", columnVisibility.transactionType)}
                                        {renderSortableHeader("currency", "Currency", columnVisibility.currency)}
                                        {renderSortableHeader("amount", "Gross", columnVisibility.amount, "text-right")}
                                        {renderSortableHeader("outstandingAmount", "Outstanding", columnVisibility.outstandingAmount, "text-right")}
                                        {renderSortableHeader("status", "Status", columnVisibility.status)}
                                        {columnVisibility.description && <TableHead>Description</TableHead>}
                                        {columnVisibility.createdBy && <TableHead>Created By</TableHead>}
                                        {columnVisibility.modifiedBy && <TableHead>Modified By</TableHead>}
                                        {renderSortableHeader("accountCode", "Account", columnVisibility.accountCode)}
                                        {renderSortableHeader("netAmount", "Net", columnVisibility.netAmount, "text-right")}
                                        {renderSortableHeader("vatAmount", "Tax", columnVisibility.vatAmount, "text-right")}
                                        {columnVisibility.flags && <TableHead>Flags</TableHead>}
                                        {renderSortableHeader("onDispute", "On Dispute", columnVisibility.onDispute)}
                                        {renderSortableHeader("subLedger", "Sub Ledger", columnVisibility.subLedger)}
                                        {renderSortableHeader("orderNumber", "Order Number", columnVisibility.orderNumber)}
                                        {renderSortableHeader("currencyGross", "Currency Gross", columnVisibility.currencyGross, "text-right")}
                                        {renderSortableHeader("currencyNet", "Currency Net", columnVisibility.currencyNet, "text-right")}
                                        {renderSortableHeader("currencyVAT", "Currency VAT", columnVisibility.currencyVAT, "text-right")}
                                        {renderSortableHeader("currencyOutstanding", "Currency Outstanding", columnVisibility.currencyOutstanding, "text-right")}
                                    </>
                                )
                            }
                            {showCompanyIdColumn && (
                                <TableHead>Company ID</TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedTransactions.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={visibleColumnCount}
                                    className="h-24 text-center"
                                >
                                    No transactions found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedTransactions.map(renderTransactionRow)
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {paginatedTransactions.length > 0 && (
                <div className="flex items-center justify-between px-2 py-4">
                    <div className="text-sm text-muted-foreground">
                        Showing {(currentPage - 1) * pageSize + 1} to{" "}
                        {Math.min(currentPage * pageSize, effectiveTotalItems)} of{" "}
                        {effectiveTotalItems} results
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Rows per page:</span>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 gap-1">
                                        {pageSize}
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {[10, 25, 50, 100].map((size) => (
                                        <DropdownMenuItem
                                            key={size}
                                            onClick={() => {
                                                onPageSizeChange?.(size);
                                                onPageChange?.(1);
                                            }}
                                        >
                                            {size}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                                Page {currentPage} of {totalPages}
                            </span>
                            <div className="flex gap-1">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onPageChange?.(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onPageChange?.(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
